import React from 'react';
import _ from 'lodash';
import { DetailTabType, MockServiceAnalyticsState } from '../../../../store/analytics/ui/analyticsUiTypes';
import { MsAnalyticsDataState, TabDataState } from '../../../../store/analytics/data/analyticsDataTypes';
import { BlazeTableColDef, BlazeTableOptions } from '../../../../components/blazeTable/BlazeTable.types';
import i18n from '../../../../i18n';
import styles from '../Inspection/InspectionViewTransactional.module.css';
import { HttpCallDetailsType, HttpRequest } from '../../../../types/rest/analyticsTypes';
import { getColoredHttpMethod } from '../../../../utils/commonUtil';
import CopyToClipboardButton from '../../../../components/buttons/CopyToClipboardButton';
import BzmIcon from '../../../../components/icons/BzmIcon';
import { formatDateTimeFromMillis, formatDateTimeFromSeconds } from '../../../../utils/dateUtil';
import { isObjectContainingString } from '../../../../utils/filterUtils';
import HttpCallDetails from './HttpCallDetails';

/**
 * This file contains shared components / utilities for HTTP Calls / Webhooks table view.
 */
export type HttpViewType = 'httpCall' | 'webhook';

//Sub views in redux ui state
export type HttpUiContextType = 'webhookView' | 'httpCallView';

/**
 * Gets common data from UI context (for Webhook / HTTP call tabs)
 * @param uiContext
 */
export const getCommonDataFromUiContext = (
  uiContext: MockServiceAnalyticsState,
  viewName: 'webhookView' | 'httpCallView' | 'stateChangesView',
) => {
  return {
    activePage: uiContext ? uiContext[viewName].activePage : 1,
    pageSize: uiContext ? uiContext[viewName].pageSize : 5,
    selectedHttpReq: viewName !== 'stateChangesView' && uiContext ? uiContext[viewName].selectedHttpRequest : null,
    searchKeyword: uiContext ? uiContext[viewName].searchKeyword : '',
  };
};

/**
 * Gets common data from data context (shared between Webhook / HTTP calls tab)
 * @param dataContext
 * @param selectedContext
 */
export const getCommonDataFromDataContext = (
  dataContext: MsAnalyticsDataState,
  selectedContext: 'httpCall' | 'webhook' | 'stateChanges',
) => {
  const isContextReady = dataContext !== null && dataContext[selectedContext] !== null;
  const lastRefresh =
    isContextReady && dataContext[selectedContext].lastRefreshTs !== null
      ? new Date(dataContext[selectedContext].lastRefreshTs)
      : null;

  return {
    isRequestListLoading: dataContext ? dataContext[selectedContext].isLoadingData : false,
    lastRefresh: lastRefresh,
    tabId: dataContext ? dataContext.tabId : null,
  };
};

// Definition of table columns
export const getColsDefinition = (viewType: HttpViewType): BlazeTableColDef[] => [
  {
    header: i18n.t('analytics:webhook.table.name'),
    width: 200,
    accessor: 'webHookName',
  },
  {
    header: i18n.t('analytics:webhook.table.method'),
    width: 80,
    accessor: 'operation',
    cellComponent: (httpRequest: HttpRequest) => {
      return getColoredHttpMethod(httpRequest.operation as any);
    },
  },
  {
    header:
      viewType === 'webhook' ? i18n.t('analytics:webhook.table.callbackUrl') : i18n.t('analytics:httpCalls.table.url'),
    cellComponent: (httpRequest: HttpRequest) => {
      return (
        <div id={`requestPathValue` + httpRequest.id} title={httpRequest.url}>
          {httpRequest.url}
          <div className="flex-table-actions ml-2">
            <CopyToClipboardButton
              textToCopy={httpRequest.url}
              title={i18n.t('analytics:webhook.table.copyRequestPath')}
              id={`requestPathCopyButton` + httpRequest.id}
            >
              <BzmIcon variant={'icon-bzm-copy'} className="btn-link-dark icon-xs text-blue" aria-hidden="true" />
            </CopyToClipboardButton>
          </div>
        </div>
      );
    },
    accessor: 'url',
  },
  {
    header: i18n.t('analytics:webhook.table.trigger'),
    accessor: 'timestamp',
    width: 180,
    cellComponent: (webHook: HttpRequest) => {
      if (_.isNil(webHook.timestamp)) {
        return 'N/A';
      }
      const dateTimeWithMillis = formatDateTimeFromMillis(parseInt(webHook.timestamp));
      const dateTimeWithSeconds = formatDateTimeFromSeconds(parseInt(webHook.timestamp) / 1000, true);
      return <span title={dateTimeWithMillis}>{dateTimeWithSeconds}</span>;
    },
  },
];

export const getHttpViewTableOptions = (
  onRowClick: any,
  selectedHttpReq: any,
  paginationOverrides,
  viewType: HttpViewType,
): BlazeTableOptions => {
  return {
    showRowExpander: false,
    columnDef: getColsDefinition(viewType),
    pageable: true,
    selectable: false,
    pageSize: 5,
    minWidth: 950,
    minHeight: 210,
    tableName: 'httpCallsList',
    noResultsText: i18n.t('analytics:inspection.noResultsText'),
    customHeaderClassName: 'flex-table-item-top',
    customTableClassName: 'flex-table-simple flex-table-condensed',
    sortable: true,
    onRowClick: (httpCallId: number) => onRowClick(httpCallId),
    customRowClassName: (request: any) => {
      let rowClass = 'hand-cursor ';
      if (selectedHttpReq && request.id.toString() === selectedHttpReq.id.toString()) {
        rowClass += styles.customEventClass;
      }
      return rowClass;
    },
    maxContentHeight: '186px',
    paginationOverride: paginationOverrides,
    hidePager: paginationOverrides.total === 0,
  };
};

/**
 * Get filtered requests based on keyword.
 * @param searchKeyword
 * @param httpReqItems
 */
export const getFilteredHttpRequests = (searchKeyword: string, httpReqItems: any[]) => {
  if (!_.isEmpty(searchKeyword)) {
    const fieldsToSearch = ['name', 'webHookName', 'operation', 'url', 'id', 'transactionId'];
    return httpReqItems.filter((item: HttpRequest) => {
      return isObjectContainingString(searchKeyword, item, fieldsToSearch);
    });
  } else {
    return httpReqItems;
  }
};

/**
 * Builds detail component for WebHook / HTTP Call.
 * @param activeUiContext
 * @param viewType
 * @param activeDataContext
 * @param onRefreshHandler
 */
export const getHttpCallDetail = (
  selectHttpCallsDetailTab: (tabName: DetailTabType) => void,
  activeUiContext: MockServiceAnalyticsState,
  viewType: HttpViewType,
  activeDataContext: TabDataState,
  onRefreshHandler: (mockServiceId: string | number) => void,
) => {
  const uiContextName: HttpUiContextType = viewType === 'webhook' ? 'webhookView' : 'httpCallView';
  const selectedLevel = activeUiContext[uiContextName].selectedLevelTab;
  const selectedTab = activeUiContext[uiContextName].selectedViewTab;
  const selectedHttpRequest = activeUiContext[uiContextName].selectedHttpRequest as HttpRequest;

  const tabId = activeDataContext.tabId;
  const httpCallState = activeDataContext[viewType];
  const isOutdated = httpCallState ? httpCallState.isOutdated : false;
  const isLoading = httpCallState ? httpCallState.isLoadingDetails : false;

  const httpCallDetails = httpCallState.detail as HttpCallDetailsType;
  let details = httpCallDetails ? httpCallDetails.request : null;
  if (selectedTab === 'secondTab') {
    details = httpCallDetails ? httpCallDetails.response : null;
  }

  return (
    <HttpCallDetails
      selectedLevel={selectedLevel}
      selectedTab={selectedTab}
      tabId={tabId}
      isLoading={isLoading}
      details={details}
      isOutdated={isOutdated}
      onRefresh={onRefreshHandler}
      httpRequest={selectedHttpRequest}
      viewType={viewType}
      selectHttpCallsDetailTab={selectHttpCallsDetailTab}
    />
  );
};
